import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api, { BACKEND_URL } from '../api';
import { useAuth } from '../AuthContext';
import { useToast } from '../Toast';
import { useStatuses } from '../useStatuses';

function FileIcon({ mimeType }) {
  if (!mimeType) return <div className="file-icon other">?</div>;
  if (mimeType.includes('pdf')) return <div className="file-icon pdf">PDF</div>;
  if (mimeType.includes('word') || mimeType.includes('document')) return <div className="file-icon doc">DOC</div>;
  if (mimeType.startsWith('video/')) return <div className="file-icon vid">▶</div>;
  if (mimeType.startsWith('image/')) return <div className="file-icon img">IMG</div>;
  return <div className="file-icon other">FILE</div>;
}

function formatBytes(b) {
  if (!b) return '';
  if (b < 1024) return b + ' B';
  if (b < 1024 * 1024) return (b / 1024).toFixed(0) + ' KB';
  return (b / 1024 / 1024).toFixed(1) + ' MB';
}

// Renders comment content — obsługuje HTML z rich editora oraz czysty tekst
function CommentText({ text }) {
  if (!text) return null;
  // Jeśli wygląda jak HTML (zawiera tagi) — renderuj jako innerHTML
  const isHtml = /<[a-z][\s\S]*>/i.test(text);
  if (isHtml) {
    return (
      <div
        className="comment-html-content"
        dangerouslySetInnerHTML={{ __html: text }}
        style={{ lineHeight: 1.6, fontSize: 14, wordBreak: 'break-word' }}
      />
    );
  }
  // Czysty tekst z linkami
  const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`[\]]+|(?:www\.|[a-zA-Z0-9-]+\.(?:pl|com|net|org|eu|io|de|fr|uk|us))[^\s<>"{}|\\^`[\]]*)/gi;
  const parts = [];
  let last = 0;
  let match;
  while ((match = urlRegex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    let url = match[0];
    if (!url.startsWith('http')) url = 'https://' + url;
    parts.push(
      <a key={match.index} href={url} target="_blank" rel="noopener noreferrer"
        style={{ color: 'var(--primary)', textDecoration: 'underline' }}>
        {match[0]}
      </a>
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <span style={{ whiteSpace: 'pre-wrap' }}>{parts}</span>;
}

// ─── Rich Comment Editor (Jira-style) ───────────────────────────────────────
const TOOLBAR_BTNS = [
  { cmd: 'bold',          icon: <><b>B</b></>,           title: 'Pogrubienie (Ctrl+B)' },
  { cmd: 'italic',        icon: <><i>I</i></>,            title: 'Kursywa (Ctrl+I)' },
  { cmd: 'underline',     icon: <><u>U</u></>,            title: 'Podkreślenie (Ctrl+U)' },
  { cmd: 'insertUnorderedList', icon: <>&#8226;&#8202;&#8226;&#8202;&#8226;</>, title: 'Lista punktowa' },
  { cmd: 'insertOrderedList',   icon: <>1&#46;&#8202;2&#46;&#8202;3&#46;</>,   title: 'Lista numerowana' },
  { cmd: 'formatBlock',   arg: 'pre', icon: <code style={{fontFamily:'monospace',fontSize:11}}>&lt;/&gt;</code>, title: 'Kod' },
];

function RichCommentEditor({
  allUsers, onSubmit, submitting,
  commentImages, setCommentImages, commentImageRef,
  comment, setComment,
  mentionQuery, setMentionQuery, mentionPos, setMentionPos,
  filteredMentions, insertMention,
  handleCommentImagePaste, handleCommentImageDrop, commentTextRef,
}) {
  const editorRef = useRef(null);
  const [focused, setFocused] = React.useState(false);

  // Sync contentEditable → comment state
  const onInput = (e) => {
    const val = e.currentTarget.innerHTML;
    setComment(val);

    // @mention detection via text content
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType !== Node.TEXT_NODE) return;
    const textBefore = node.textContent.slice(0, range.startOffset);
    const match = textBefore.match(/@(\w*)$/);
    if (match) {
      setMentionQuery(match[1]);
      const rect = range.getBoundingClientRect();
      setMentionPos({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX });
    } else {
      setMentionQuery('');
      setMentionPos(null);
    }
  };

  const execCmd = (cmd, arg) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg || null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      doSubmit();
    }
    if (e.key === 'Escape') { setMentionQuery(''); setMentionPos(null); }
  };

  const doSubmit = () => {
    const html = editorRef.current?.innerHTML || '';
    const text = editorRef.current?.innerText || '';
    if (!text.trim() && commentImages.length === 0) return;
    // przekaż html jako content
    onSubmit(null, html);
  };

  // Wstaw mention do edytora
  const insertMentionInEditor = (name) => {
    editorRef.current?.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      const offset = range.startOffset;
      const before = text.slice(0, offset);
      const match = before.match(/@(\w*)$/);
      if (match) {
        const start = offset - match[0].length;
        node.textContent = text.slice(0, start) + text.slice(offset);
        range.setStart(node, start);
        range.setEnd(node, start);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
    // wstaw span z oznaczeniem
    const span = document.createElement('span');
    span.style.color = 'var(--primary)';
    span.style.fontWeight = '600';
    span.contentEditable = 'false';
    span.textContent = `@${name}`;
    range.insertNode(span);
    // przesuń kursor za span
    const after = document.createTextNode(' ');
    span.after(after);
    const newRange = document.createRange();
    newRange.setStart(after, 1);
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);
    setMentionQuery('');
    setMentionPos(null);
    setComment(editorRef.current?.innerHTML || '');
  };

  // paste: przechwytaj obrazki
  const onPaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imgItems = items.filter(it => it.type.startsWith('image/'));
    if (imgItems.length === 0) return;
    e.preventDefault();
    imgItems.forEach(it => {
      const file = it.getAsFile();
      if (file) {
        const preview = URL.createObjectURL(file);
        setCommentImages(prev => [...prev, { file, preview }]);
      }
    });
  };

  const onDrop = (e) => {
    e.preventDefault();
    Array.from(e.dataTransfer?.files || []).filter(f => f.type.startsWith('image/')).forEach(file => {
      const preview = URL.createObjectURL(file);
      setCommentImages(prev => [...prev, { file, preview }]);
    });
  };

  const isEmpty = !comment || comment === '<br>' || comment === '<div><br></div>';

  const toolbarStyle = {
    display: 'flex', alignItems: 'center', gap: 2,
    padding: '6px 10px',
    borderBottom: '1px solid var(--gray-200)',
    background: 'var(--gray-50)',
    borderRadius: '8px 8px 0 0',
    flexWrap: 'wrap',
  };
  const tbBtnStyle = (active) => ({
    border: 'none', background: active ? 'var(--gray-200)' : 'transparent',
    borderRadius: 4, cursor: 'pointer', padding: '4px 7px',
    fontSize: 13, color: 'var(--gray-700)', lineHeight: 1,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minWidth: 28, height: 26,
    transition: 'background .1s',
  });

  return (
    <div style={{ position: 'relative', marginTop: 8 }}>
      <div style={{
        border: `1.5px solid ${focused ? 'var(--primary)' : 'var(--gray-200)'}`,
        borderRadius: 8,
        background: 'white',
        transition: 'border-color .15s',
        overflow: 'visible',
      }}>
        {/* Toolbar */}
        <div style={toolbarStyle}>
          {TOOLBAR_BTNS.map(btn => (
            <button key={btn.cmd + (btn.arg || '')} type="button" title={btn.title}
              onMouseDown={e => { e.preventDefault(); execCmd(btn.cmd, btn.arg); }}
              style={tbBtnStyle(false)}>
              {btn.icon}
            </button>
          ))}
          <div style={{ width: 1, height: 18, background: 'var(--gray-200)', margin: '0 4px' }} />
          <button type="button" title="Dodaj zdjęcia" onMouseDown={e => { e.preventDefault(); commentImageRef.current?.click(); }}
            style={{ ...tbBtnStyle(false), fontSize: 15 }}>
            🖼
          </button>
          <button type="button" title="Wspomnij osobę (@)" onMouseDown={e => {
            e.preventDefault();
            editorRef.current?.focus();
            document.execCommand('insertText', false, '@');
          }} style={{ ...tbBtnStyle(false), fontSize: 13, fontWeight: 600 }}>
            @
          </button>
        </div>

        {/* Editable area */}
        <div style={{ position: 'relative' }}>
          {isEmpty && !focused && (
            <div style={{
              position: 'absolute', top: 12, left: 14, pointerEvents: 'none',
              color: 'var(--gray-400)', fontSize: 14, zIndex: 1
            }}>
              Napisz komentarz... (wklej lub przeciągnij zdjęcie)
            </div>
          )}
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            onInput={onInput}
            onKeyDown={handleKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onPaste={onPaste}
            onDrop={onDrop}
            onDragOver={e => e.preventDefault()}
            style={{
              minHeight: 80, maxHeight: 320, overflowY: 'auto',
              padding: '10px 14px', outline: 'none',
              fontSize: 14, lineHeight: 1.6, color: 'var(--gray-800)',
              fontFamily: 'inherit',
            }}
          />
        </div>

        {/* Image previews inside box */}
        {commentImages.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '8px 14px', borderTop: '1px solid var(--gray-100)' }}>
            {commentImages.map((img, i) => (
              <div key={i} style={{ position: 'relative' }}>
                <img src={img.preview} alt="" style={{ width: 72, height: 54, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--gray-200)', display: 'block' }} />
                <button onMouseDown={e => { e.preventDefault(); setCommentImages(prev => prev.filter((_, j) => j !== i)); }}
                  style={{ position: 'absolute', top: -5, right: -5, width: 17, height: 17, borderRadius: '50%', background: '#ef4444', color: 'white', border: 'none', cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>×</button>
              </div>
            ))}
          </div>
        )}

        {/* Footer: hint + Send button */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '8px 10px', borderTop: '1px solid var(--gray-100)',
          background: 'var(--gray-50)', borderRadius: '0 0 8px 8px',
        }}>
          <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>Ctrl+Enter aby wysłać</span>
          <button className="btn btn-primary" type="button"
            disabled={submitting || (isEmpty && commentImages.length === 0)}
            onClick={doSubmit}
            style={{ height: 32, padding: '0 16px', fontSize: 13 }}>
            {submitting ? 'Wysyłanie...' : 'Zapisz'}
          </button>
        </div>
      </div>

      {/* @mention dropdown */}
      {mentionPos && filteredMentions.length > 0 && (
        <div style={{
          position: 'fixed',
          top: mentionPos.top, left: mentionPos.left,
          background: 'white', border: '1px solid var(--gray-200)',
          borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,.15)',
          zIndex: 9999, minWidth: 220, maxHeight: 220, overflowY: 'auto',
        }}>
          {filteredMentions.map(u => (
            <div key={u.id} onMouseDown={() => insertMentionInEditor(u.name)}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}
              onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
              onMouseLeave={e => e.currentTarget.style.background = 'white'}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                {(u.name || '?').split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2)}
              </div>
              <div>
                <div style={{ fontWeight: 600, color: 'var(--gray-800)' }}>{u.name}</div>
                <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{u.email}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <input ref={commentImageRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
        onChange={e => {
          Array.from(e.target.files).forEach(file => {
            const preview = URL.createObjectURL(file);
            setCommentImages(prev => [...prev, { file, preview }]);
          });
          e.target.value = '';
        }} />
    </div>
  );
}

// ─── Attachment preview row ──────────────────────────────────────────────────
function AttachmentPreviewRow({ att, onDownload, backendUrl }) {
  const [showPreview, setShowPreview] = React.useState(false);
  const token = localStorage.getItem('token');
  const mime = att.mime_type || '';
  const name = (att.original_name || '').toLowerCase();
  const isPdf = mime.includes('pdf') || name.endsWith('.pdf');
  const isImage = mime.startsWith('image/');
  const isDocx = mime.includes('wordprocessingml') || name.endsWith('.docx') || name.endsWith('.doc');
  const canPreview = isPdf || isImage || isDocx;

  return (
    <div style={{ borderTop: '1px solid var(--gray-100)', paddingTop: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <FileIcon mimeType={att.mime_type} />
        <div style={{ flex: 1, fontSize: 13, color: 'var(--gray-700)', fontWeight: 500 }}>{att.original_name}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {canPreview && (
            <button
              onClick={() => setShowPreview(v => !v)}
              className="btn btn-secondary btn-sm"
              style={{ fontWeight: 500 }}>
              {showPreview ? '✕ Zamknij' : '👁 Podgląd'}
            </button>
          )}
          <button onClick={() => onDownload(att.id, att.original_name)} className="btn btn-primary btn-sm" style={{ fontWeight: 600 }}>
            ⬇ Pobierz
          </button>
        </div>
      </div>

      {showPreview && (
        <div style={{ marginTop: 12, border: '1px solid var(--gray-200)', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
          {isImage && (
            <img
              src={`${backendUrl}/api/attachments/${att.id}/preview?token=${token}`}
              alt={att.original_name}
              style={{ maxWidth: '100%', display: 'block' }}
            />
          )}
          {(isPdf || isDocx) && (
            <iframe
              src={`${backendUrl}/api/attachments/${att.id}/preview?authtoken=${encodeURIComponent(token)}`}
              title={att.original_name}
              style={{ width: '100%', height: 600, border: 'none', display: 'block' }}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default function ProcedureDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isPrintMode = searchParams.get('print') === '1';
  const [proc, setProc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('comments');
  const [comment, setComment] = useState('');
  const [commentImages, setCommentImages] = useState([]); // { file, preview }
  const [submittingComment, setSubmittingComment] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const [statusModal, setStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusComment, setStatusComment] = useState('');
  const [missingInfo, setMissingInfo] = useState('');
  const [changingStatus, setChangingStatus] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionPos, setMentionPos] = useState(null); // { top, left }
  const [allUsers, setAllUsers] = useState([]);
  const fileInputRef = useRef();
  const commentImageRef = useRef();
  const commentTextRef = useRef();
  const pollRef = useRef();
  const { user, isAdmin, isDeptHead } = useAuth();
  const toast = useToast();
  const nav = useNavigate();
  const { labelMap: STATUS_LABELS, colorMap: STATUS_COLORS, statuses: allStatuses } = useStatuses();

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/procedures/${id}`);
      setProc(r.data);
      return r.data;
    } catch {
      toast('Błąd ładowania procedury', 'error');
      nav('/procedures');
    }
  }, [id]); // eslint-disable-line

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
    // Pobierz listę użytkowników do @wzmianek
    api.get('/auth/users').then(r => setAllUsers(r.data || [])).catch(() => {});
    return () => clearInterval(pollRef.current);
  }, [load]);

  // Poll for pending AI summaries
  useEffect(() => {
    const hasPending = proc?.attachments?.some(a => a.summary_status === 'pending' || a.summary_status === 'processing' || a.summary_status === 'generating');
    if (hasPending && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const updated = await load();
        const stillPending = updated?.attachments?.some(a => a.summary_status === 'pending' || a.summary_status === 'processing' || a.summary_status === 'generating');
        if (!stillPending) { clearInterval(pollRef.current); pollRef.current = null; }
      }, 3000);
    }
    return () => { if (!hasPending && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [proc, load]);

  async function handleDelete() {
    if (!window.confirm('Na pewno usunąć tę procedurę?')) return;
    try {
      await api.delete(`/procedures/${id}`);
      toast('Usunięto procedurę');
      nav('/procedures');
    } catch (err) {
      toast(err.response?.data?.error || 'Błąd usuwania', 'error');
    }
  }

  async function handleComment(e, htmlContent) {
    if (e) e.preventDefault();
    // htmlContent z RichCommentEditor, fallback do comment state
    const content = htmlContent !== undefined ? htmlContent : comment;
    const textContent = content.replace(/<[^>]+>/g, '').trim();
    if (!textContent && commentImages.length === 0) return;
    setSubmittingComment(true);
    try {
      const form = new FormData();
      form.append('content', content);
      commentImages.forEach(img => form.append('images', img.file));
      await api.post(`/comments/${id}`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setComment('');
      setCommentImages([]);
      setMentionQuery('');
      setMentionPos(null);
      toast('Komentarz dodany');
      load();
    } catch {
      toast('Błąd dodawania komentarza', 'error');
    } finally {
      setSubmittingComment(false);
    }
  }

  function handleCommentInput(e) {
    const val = e.target.value;
    setComment(val);
    // Wykryj @
    const cursor = e.target.selectionStart;
    const before = val.slice(0, cursor);
    const match = before.match(/@(\w*)$/);
    if (match) {
      setMentionQuery(match[1]);
      const rect = e.target.getBoundingClientRect();
      setMentionPos({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX });
    } else {
      setMentionQuery('');
      setMentionPos(null);
    }
  }

  function insertMention(userName) {
    const el = commentTextRef.current;
    const cursor = el.selectionStart;
    const before = comment.slice(0, cursor);
    const after = comment.slice(cursor);
    const newBefore = before.replace(/@\w*$/, `@${userName} `);
    setComment(newBefore + after);
    setMentionQuery('');
    setMentionPos(null);
    setTimeout(() => { el.focus(); el.setSelectionRange(newBefore.length, newBefore.length); }, 0);
  }

  function handleCommentImagePaste(e) {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        const preview = URL.createObjectURL(file);
        setCommentImages(prev => [...prev, { file, preview }]);
      }
    }
  }

  function handleCommentImageDrop(e) {
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    files.forEach(file => {
      const preview = URL.createObjectURL(file);
      setCommentImages(prev => [...prev, { file, preview }]);
    });
  }

  async function handleUpload(files) {
    if (!files || files.length === 0) return;
    setUploading(true);
    const form = new FormData();
    for (const file of files) {
      form.append('files', file);
    }
    form.append('procedure_id', id);
    try {
      await api.post('/attachments/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast('Plik(i) wgrane — analizuję treść...');
      load();
    } catch (err) {
      toast(err.response?.data?.error || 'Błąd uploadu', 'error');
    } finally {
      setUploading(false);
    }
  }

  async function handleDeleteAttachment(attId) {
    if (!window.confirm('Usunąć załącznik?')) return;
    await api.delete(`/attachments/${attId}`);
    toast('Załącznik usunięty');
    load();
  }

  async function handleDownload(attId, originalName) {
    try {
      const r = await api.get(`/attachments/${attId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = originalName || attId;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast('Błąd pobierania pliku', 'error');
    }
  }

  async function handleStatusChange(e) {
    e.preventDefault();
    if (!newStatus) return;
    setChangingStatus(true);
    try {
      await api.post(`/procedures/${id}/status`, {
        status: newStatus,
        comment: statusComment,
        missing_info: newStatus === 'needs_info' ? missingInfo : undefined
      });
      toast('Status zmieniony');
      setStatusModal(false);
      setNewStatus('');
      setStatusComment('');
      setMissingInfo('');
      load();
    } catch (err) {
      toast(err.response?.data?.error || 'Błąd zmiany statusu', 'error');
    } finally {
      setChangingStatus(false);
    }
  }

  async function handleNewVersion() {
    if (!window.confirm('Utworzyć nową wersję roboczą tej procedury?')) return;
    try {
      const r = await api.post(`/procedures/${id}/new-version`);
      toast('Nowa wersja utworzona jako szkic');
      nav(`/procedures/${r.data.id}`);
    } catch (err) {
      toast(err.response?.data?.error || 'Błąd', 'error');
    }
  }

  if (loading) return <div style={{ padding: 32, color: 'var(--gray-400)' }}>Ładowanie...</div>;
  if (!proc) return null;

  // ── Print / PDF mode ──────────────────────────────────────────
  if (isPrintMode) {
    return (
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '40px 32px', fontFamily: 'Arial, sans-serif', color: '#111' }}>
        <style>{`@media print { button { display: none !important; } }`}</style>
        <div style={{ marginBottom: 24, paddingBottom: 16, borderBottom: '2px solid #333' }}>
          <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Baza Wiedzy · Procedura</div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 8px' }}>{proc.title}</h1>
          <div style={{ display: 'flex', gap: 20, fontSize: 13, color: '#555', flexWrap: 'wrap' }}>
            {proc.owner_name && <span>👤 <strong>Właściciel:</strong> {proc.owner_name}</span>}
            <span>📋 <strong>Status:</strong> {STATUS_LABELS[proc.status] || proc.status}</span>
            <span>🔢 <strong>Wersja:</strong> v{proc.current_version || 1}</span>
            {proc.updated_at && <span>📅 <strong>Aktualizacja:</strong> {new Date(proc.updated_at).toLocaleDateString('pl-PL')}</span>}
          </div>
          {(proc.departments || []).length > 0 && (
            <div style={{ marginTop: 6, fontSize: 13, color: '#555' }}>🗂️ <strong>Działy:</strong> {proc.departments.map(d => d.name).join(', ')}</div>
          )}
        </div>
        {proc.description && (
          <div style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#333', marginBottom: 6 }}>Opis</h3>
            <p style={{ fontSize: 14, lineHeight: 1.7, color: '#444', whiteSpace: 'pre-wrap' }}>{proc.description}</p>
          </div>
        )}
        {proc.ai_summary && (
          <div style={{ marginBottom: 20, padding: '12px 16px', background: '#f0f4ff', borderRadius: 6, border: '1px solid #c7d7f0' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#4158b0', marginBottom: 4 }}>🤖 Streszczenie AI</div>
            <p style={{ margin: 0, fontSize: 13.5, color: '#333', lineHeight: 1.6 }}>{proc.ai_summary}</p>
          </div>
        )}
        {(proc.attachments || []).length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#333', marginBottom: 8 }}>Załączniki ({proc.attachments.length})</h3>
            {proc.attachments.map(a => (
              <div key={a.id} style={{ padding: '8px 0', borderBottom: '1px solid #eee', fontSize: 13, color: '#444' }}>
                📎 {a.file_name}
              </div>
            ))}
          </div>
        )}
        <div style={{ marginTop: 32, paddingTop: 16, borderTop: '1px solid #ddd', fontSize: 11, color: '#999', display: 'flex', justifyContent: 'space-between' }}>
          <span>Wydrukowano: {new Date().toLocaleString('pl-PL')}</span>
          <span>bazawiedzy.wheeltrade.pl</span>
        </div>
        <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
          <button onClick={() => window.print()} style={{ padding: '10px 20px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
            🖨️ Drukuj / Zapisz jako PDF
          </button>
          <button onClick={() => window.close()} style={{ padding: '10px 20px', background: '#f3f4f6', color: '#333', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}>
            Zamknij
          </button>
        </div>
      </div>
    );
  }

  const canEdit = isAdmin || isDeptHead || proc.author_id === user?.id || proc.owner_id === user?.id || proc.updater_id === user?.id;
  const isOwner = proc.owner_id === user?.id || proc.updater_id === user?.id;

  // What status transitions can this user make?
  const myTransitions = (() => {
    const role = user?.role;
    const trans = proc.allowed_transitions || {};
    const allowed = new Set([
      ...(trans[role] || []),
      ...(isOwner ? (trans['owner'] || []) : [])
    ]);
    return [...allowed];
  })();

  const renderMiroUrl = (url) => {
    if (!url) return null;
    const m = url.match(/src="([^"]+)"/);
    return m ? m[1] : url;
  };

  const statusBadgeStyle = {
    background: STATUS_COLORS[proc.status] + '22',
    color: STATUS_COLORS[proc.status],
    border: `1px solid ${STATUS_COLORS[proc.status]}44`,
    borderRadius: 6,
    padding: '2px 10px',
    fontWeight: 600,
    fontSize: 12
  };

  const tabs = [
    { key: 'comments', label: `💬 Komentarze (${proc.comments?.length || 0})` },
    { key: 'attachments', label: `📎 Załączniki (${proc.attachments?.length || 0})` },
    { key: 'status-history', label: `Historia (${proc.status_history?.length || 0})` },
    proc.children?.length > 0 && { key: 'subproc', label: `Podprocedury (${proc.children.length})` },
    proc.miro_url && { key: 'miro', label: 'Miro / SIPOC' },
  ].filter(Boolean);

  const filteredMentions = mentionQuery !== null && mentionQuery !== undefined
    ? allUsers.filter(u => u.name?.toLowerCase().includes(mentionQuery.toLowerCase()) ||
        u.email?.toLowerCase().includes(mentionQuery.toLowerCase())).slice(0, 6)
    : [];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 20 }}>
        <button className="btn btn-secondary btn-sm" onClick={() => nav('/procedures')}>← Wróć</button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--gray-900)', margin: 0 }}>{proc.title}</h1>
            <span style={statusBadgeStyle}>{STATUS_LABELS[proc.status] || proc.status}</span>
            {proc.priority && proc.priority !== 'normal' && (
              <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                {proc.priority === 'high' ? '🔴 Wysoki priorytet' : '🔵 Niski priorytet'}
              </span>
            )}
          </div>

          {/* People info */}
          <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap', fontSize: 13 }}>
            {proc.owner_name && (
              <span style={{ color: 'var(--gray-600)' }}>
                <span style={{ color: 'var(--gray-400)' }}>Właściciel: </span>
                <strong>{proc.owner_name}</strong>
              </span>
            )}
            {proc.author_name && (
              <span style={{ color: 'var(--gray-600)' }}>
                <span style={{ color: 'var(--gray-400)' }}>Autor: </span>
                {proc.author_name}
              </span>
            )}
            {proc.updater_name && (
              <span style={{ color: 'var(--gray-600)' }}>
                <span style={{ color: 'var(--gray-400)' }}>Aktualizujący: </span>
                <strong>{proc.updater_name}</strong>
              </span>
            )}
            {proc.executors?.length > 0 && (
              <span style={{ color: 'var(--gray-600)' }}>
                <span style={{ color: 'var(--gray-400)' }}>Wykonujący: </span>
                {proc.executors.map((e, i) => (
                  <span key={i}>{i > 0 ? ', ' : ''}<strong>{e.group_name || e.user_name}</strong></span>
                ))}
              </span>
            )}
          </div>

          {proc.departments?.length > 0 && (
            <div style={{ marginTop: 6 }}>
              {proc.departments.map(d => (
                <span key={d.id} className="tag">{d.name}</span>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {canEdit && (
            <button className="btn btn-secondary btn-sm" onClick={() => nav(`/procedures/${id}/edit`)}>Edytuj</button>
          )}
          {myTransitions.length > 0 && (
            <button className="btn btn-primary btn-sm" onClick={() => setStatusModal(true)}>
              Zmień status
            </button>
          )}
          {proc.status === 'approved' && canEdit && (
            <button className="btn btn-secondary btn-sm" onClick={handleNewVersion}>Nowa wersja</button>
          )}
          {isAdmin && (
            <button className="btn btn-danger btn-sm" onClick={handleDelete}>Usuń</button>
          )}
        </div>
      </div>

      {/* Reminder banner */}
      {proc.review_reminder_date && (
        <div style={{ background: '#fef9c3', border: '1px solid #fde047', borderRadius: 'var(--radius)', padding: '8px 14px', marginBottom: 16, fontSize: 13, color: '#713f12', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span>🔔 Przypomnienie o przeglądzie: {new Date(proc.review_reminder_date).toLocaleDateString('pl-PL')}
          {proc.review_reminder_time ? `, godz. ${proc.review_reminder_time}` : ''}</span>
          {canEdit && (
            <button
              onClick={async () => {
                if (!window.confirm('Usunąć przypomnienie o przeglądzie?')) return;
                try {
                  await api.put(`/procedures/${id}`, { review_reminder_date: null, review_reminder_time: null });
                  toast('Przypomnienie usunięte');
                  load();
                } catch { toast('Błąd usuwania przypomnienia', 'error'); }
              }}
              style={{ background: 'none', border: '1px solid #a16207', borderRadius: 4, color: '#a16207', padding: '2px 8px', cursor: 'pointer', fontSize: 12, flexShrink: 0 }}>
              ✕ Usuń przypomnienie
            </button>
          )}
        </div>
      )}

      {/* Status reason / missing info */}
      {proc.status === 'needs_info' && proc.missing_info && (
        <div style={{ background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#9a3412' }}>
          ⚠️ <strong>Brakujące informacje:</strong> {proc.missing_info}
        </div>
      )}

      {/* Description + AI summary + current attachment download + preview */}
      {(() => {
        const currentAtts = (proc.attachments || []).filter(a => a.is_current);
        const aiSummary = currentAtts.find(a => a.ai_summary)?.ai_summary || proc.ai_summary;
        const aiPending = currentAtts.some(a => a.summary_status === 'pending' || a.summary_status === 'processing' || a.summary_status === 'generating');
        if (!proc.content && !aiSummary && !aiPending && currentAtts.length === 0) return null;
        return (
          <div className="card" style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {proc.content && (
              <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, color: 'var(--gray-700)', lineHeight: 1.6 }}>{proc.content}</div>
            )}
            {aiPending && !aiSummary && (
              <div style={{ background: '#f0f4ff', border: '1px solid #c7d7f0', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <div className="spinner" style={{ width: 14, height: 14 }} />
                <span style={{ fontSize: 13, color: '#4158b0' }}>Generuję streszczenie AI...</span>
              </div>
            )}
            {aiSummary && (
              <div style={{ background: '#f0f4ff', border: '1px solid #c7d7f0', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#4158b0', marginBottom: 4 }}>🤖 Streszczenie AI</div>
                <div style={{ fontSize: 13.5, color: 'var(--gray-700)', lineHeight: 1.6 }}>{aiSummary}</div>
              </div>
            )}
            {currentAtts.map(att => (
              <AttachmentPreviewRow key={att.id} att={att} onDownload={handleDownload} backendUrl={BACKEND_URL} />
            ))}
          </div>
        );
      })()}

      {/* Parent procedure link */}
      {proc.parent_procedure && (
        <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--gray-500)' }}>
          Podprocedura procedury:
          <button className="btn-link" onClick={() => nav(`/procedures/${proc.parent_procedure.id}`)}>
            {proc.parent_procedure.title}
          </button>
        </div>
      )}

      {/* Sub-procedures — shown inline when present */}
      {proc.children?.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--gray-800)', marginBottom: 12 }}>
            Podprocedury ({proc.children.length})
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--gray-200)' }}>
                <th style={{ textAlign: 'left', padding: '6px 12px', color: 'var(--gray-500)', fontWeight: 600 }}>Tytuł</th>
                <th style={{ textAlign: 'left', padding: '6px 12px', color: 'var(--gray-500)', fontWeight: 600 }}>Status</th>
                <th style={{ textAlign: 'left', padding: '6px 12px', color: 'var(--gray-500)', fontWeight: 600 }}>Właściciel</th>
                <th style={{ textAlign: 'left', padding: '6px 12px', color: 'var(--gray-500)', fontWeight: 600 }}>Aktualizujący</th>
              </tr>
            </thead>
            <tbody>
              {proc.children.map(child => (
                <tr key={child.id} style={{ borderBottom: '1px solid var(--gray-100)', cursor: 'pointer' }}
                  onClick={() => nav(`/procedures/${child.id}`)}>
                  <td style={{ padding: '9px 12px', fontWeight: 500, color: 'var(--primary)' }}>{child.title}</td>
                  <td style={{ padding: '9px 12px' }}>
                    {child.status && (
                      <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: STATUS_COLORS[child.status] + '22', color: STATUS_COLORS[child.status] }}>
                        {STATUS_LABELS[child.status] || child.status}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '9px 12px', color: 'var(--gray-600)' }}>{child.owner_name || '—'}</td>
                  <td style={{ padding: '9px 12px', color: 'var(--gray-600)' }}>{child.updater_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--gray-200)', marginBottom: 20 }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer',
            borderBottom: tab === t.key ? '2px solid var(--primary)' : '2px solid transparent',
            color: tab === t.key ? 'var(--primary)' : 'var(--gray-500)',
            fontWeight: tab === t.key ? 600 : 400, fontSize: 13.5, marginBottom: -1, whiteSpace: 'nowrap'
          }}>{t.label}</button>
        ))}
      </div>

      {/* Attachments tab */}
      {tab === 'attachments' && (
        <div>
          {canEdit && (
            <div
              className={`upload-zone ${drag ? 'drag' : ''}`}
              style={{ marginBottom: 16 }}
              onDragOver={e => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={e => {
                e.preventDefault();
                setDrag(false);
                const files = Array.from(e.dataTransfer.files);
                if (files.length) handleUpload(files);
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={e => handleUpload(Array.from(e.target.files))}
              />
              <div style={{ color: 'var(--gray-400)', fontSize: 14 }}>
                {uploading ? '⏳ Wgrywanie...' : '📎 Kliknij lub przeciągnij pliki tutaj (można wybrać wiele, max 200MB każdy)'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--gray-300)', marginTop: 4 }}>
                Nowy plik Word automatycznie staje się aktualną wersją — poprzedni trafia do archiwum
              </div>
            </div>
          )}

          {(proc.attachments || []).length === 0 ? (
            <div className="empty-state"><p>Brak załączników</p></div>
          ) : (
            <>
              {(proc.attachments || []).filter(a => a.is_current).map(att => (
                <div key={att.id} className="file-item" style={{ border: '1.5px solid var(--primary)', background: 'var(--primary-light)' }}>
                  <FileIcon mimeType={att.mime_type} />
                  <div style={{ flex: 1, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600 }}>{att.original_name}</span>
                      <span className="badge badge-published">Aktualna {att.version_number > 1 ? `· wersja ${att.version_number}` : ''}</span>
                      <span className="text-muted">{formatBytes(att.size)}</span>
                    </div>
                    {(att.summary_status === 'pending' || att.summary_status === 'processing' || att.summary_status === 'generating') && (
                      <div className="ai-summary-loading"><div className="spinner"></div> Generuję streszczenie AI...</div>
                    )}
                    {att.summary_status === 'error' && <div className="text-muted" style={{ marginTop: 6 }}>⚠️ Nie udało się wygenerować streszczenia AI</div>}
                    {att.ai_summary && <div className="ai-summary-box"><strong>🤖 Streszczenie AI:</strong><br />{att.ai_summary}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'flex-start' }}>
                    <button onClick={() => handleDownload(att.id, att.original_name)} className="btn btn-primary" style={{ fontWeight: 600 }}>⬇ Pobierz</button>
                    {canEdit && <button className="btn btn-danger btn-sm" onClick={() => handleDeleteAttachment(att.id)}>✕</button>}
                  </div>
                </div>
              ))}

              {(proc.attachments || []).some(a => !a.is_current) && (
                <>
                  <div className="text-muted" style={{ margin: '16px 0 8px', fontWeight: 500 }}>Starsze wersje (archiwum)</div>
                  {(proc.attachments || []).filter(a => !a.is_current).map(att => (
                    <div key={att.id} className="file-item" style={{ opacity: 0.6 }}>
                      <FileIcon mimeType={att.mime_type} />
                      <div style={{ flex: 1, overflow: 'hidden' }}>
                        <span style={{ fontWeight: 500 }}>{att.original_name}</span>
                        <span className="text-muted" style={{ marginLeft: 8 }}>Wersja {att.version_number} · {formatBytes(att.size)} · {new Date(att.created_at).toLocaleDateString('pl-PL')}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => handleDownload(att.id, att.original_name)} className="btn btn-secondary" style={{ fontWeight: 600 }}>⬇ Pobierz</button>
                        {canEdit && <button className="btn btn-danger btn-sm" onClick={() => handleDeleteAttachment(att.id)}>✕</button>}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Comments tab */}
      {tab === 'comments' && (
        <div>
          {(proc.comments || []).length === 0 ? (
            <div className="empty-state" style={{ marginBottom: 16 }}><p>Brak komentarzy — zacznij dyskusję!</p></div>
          ) : (
            <div style={{ marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {(proc.comments || []).map(c => {
                const initials = (c.user_name || '?').split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
                const isMe = c.user_id === user?.id;
                // Parsuj obrazki z komentarza (format: [img:filename])
                const images = (c.images || []);
                return (
                  <div key={c.id} style={{
                    display: 'flex', gap: 12, alignItems: 'flex-start',
                    flexDirection: isMe ? 'row-reverse' : 'row'
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                      background: isMe ? 'var(--primary)' : 'var(--gray-200)',
                      color: isMe ? 'white' : 'var(--gray-600)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 700
                    }}>{initials}</div>
                    <div style={{ maxWidth: '75%' }}>
                      <div style={{
                        background: isMe ? 'var(--primary)' : 'var(--gray-100)',
                        color: isMe ? 'white' : 'var(--gray-800)',
                        borderRadius: isMe ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
                        padding: '10px 14px',
                        fontSize: 14, lineHeight: 1.55,
                        boxShadow: '0 1px 3px rgba(0,0,0,.07)'
                      }}>
                        {c.content && <CommentText text={c.content} />}
                        {images.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: c.content ? 8 : 0 }}>
                            {images.map((img, i) => (
                              <a key={i} href={`${BACKEND_URL}/api/comments/image/${img}`} target="_blank" rel="noreferrer">
                                <img src={`${BACKEND_URL}/api/comments/image/${img}`} alt="załącznik"
                                  style={{ maxWidth: 220, maxHeight: 160, borderRadius: 6, cursor: 'pointer', border: '1px solid rgba(0,0,0,.1)' }} />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                      <div style={{
                        fontSize: 11.5, color: 'var(--gray-400)', marginTop: 4,
                        textAlign: isMe ? 'right' : 'left', paddingInline: 4
                      }}>
                        {c.user_name} · {new Date(c.created_at).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Rich text comment editor — Jira-style */}
          <RichCommentEditor
            allUsers={allUsers}
            onSubmit={handleComment}
            submitting={submittingComment}
            commentImages={commentImages}
            setCommentImages={setCommentImages}
            commentImageRef={commentImageRef}
            comment={comment}
            setComment={setComment}
            mentionQuery={mentionQuery}
            setMentionQuery={setMentionQuery}
            mentionPos={mentionPos}
            setMentionPos={setMentionPos}
            filteredMentions={filteredMentions}
            insertMention={insertMention}
            handleCommentInput={handleCommentInput}
            handleCommentImagePaste={handleCommentImagePaste}
            handleCommentImageDrop={handleCommentImageDrop}
            commentTextRef={commentTextRef}
          />
        </div>
      )}

      {/* Status history tab */}
      {tab === 'status-history' && (
        <div>
          {(proc.status_history || []).length === 0 ? (
            <div className="empty-state"><p>Brak historii zmian statusów</p></div>
          ) : (
            <div>
              {(proc.status_history || []).map((h, i) => (
                <div key={i} style={{
                  display: 'flex', gap: 12, padding: '12px 0',
                  borderBottom: i < proc.status_history.length - 1 ? '1px solid var(--gray-100)' : 'none',
                  alignItems: 'flex-start'
                }}>
                  <div style={{
                    width: 10, height: 10, borderRadius: '50%',
                    background: STATUS_COLORS[h.to_status] || '#6b7280',
                    marginTop: 5, flexShrink: 0
                  }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, color: 'var(--gray-800)' }}>
                      <strong>{h.user_name}</strong>{' '}
                      {h.from_status ? (
                        <>zmienił status z <span style={{ color: STATUS_COLORS[h.from_status] }}>{STATUS_LABELS[h.from_status]}</span> na </>
                      ) : 'ustawił status na '}
                      <span style={{ color: STATUS_COLORS[h.to_status], fontWeight: 600 }}>{STATUS_LABELS[h.to_status]}</span>
                    </div>
                    {h.comment && <div style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 4 }}>"{h.comment}"</div>}
                    <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 4 }}>
                      {new Date(h.created_at).toLocaleString('pl-PL')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sub-procedures tab */}
      {tab === 'subproc' && proc.children?.length > 0 && (
        <div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--gray-200)' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--gray-500)', fontWeight: 600 }}>Tytuł</th>
                <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--gray-500)', fontWeight: 600 }}>Status</th>
                <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--gray-500)', fontWeight: 600 }}>Właściciel</th>
                <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--gray-500)', fontWeight: 600 }}>Aktualizujący</th>
              </tr>
            </thead>
            <tbody>
              {proc.children.map(child => (
                <tr key={child.id} style={{ borderBottom: '1px solid var(--gray-100)', cursor: 'pointer' }}
                  onClick={() => nav(`/procedures/${child.id}`)}>
                  <td style={{ padding: '10px 12px', fontWeight: 500, color: 'var(--primary)' }}>{child.title}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{
                      background: (STATUS_COLORS[child.status] || '#6b7280') + '22',
                      color: STATUS_COLORS[child.status] || '#6b7280',
                      borderRadius: 6, padding: '2px 8px', fontWeight: 600, fontSize: 11
                    }}>{STATUS_LABELS[child.status] || child.status}</span>
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--gray-600)' }}>{child.owner_name || '—'}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--gray-600)' }}>{child.updater_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Miro tab */}
      {tab === 'miro' && proc.miro_url && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <iframe
            src={renderMiroUrl(proc.miro_url)}
            className="miro-embed"
            style={{ height: 520 }}
            frameBorder="0"
            scrolling="no"
            allow="fullscreen; clipboard-read; clipboard-write"
            allowFullScreen
            title="Miro SIPOC"
          />
        </div>
      )}

      {/* Status change modal */}
      {statusModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 28, width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,.2)' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 17 }}>Zmień status procedury</h3>
            <form onSubmit={handleStatusChange}>
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label className="form-label">Nowy status</label>
                <select className="form-input" value={newStatus} onChange={e => setNewStatus(e.target.value)} required>
                  <option value="">Wybierz status...</option>
                  {myTransitions.map(s => (
                    <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                  ))}
                </select>
              </div>

              {newStatus === 'needs_info' && (
                <div className="form-group" style={{ marginBottom: 14 }}>
                  <label className="form-label">Brakujące informacje</label>
                  <textarea
                    className="form-textarea"
                    value={missingInfo}
                    onChange={e => setMissingInfo(e.target.value)}
                    placeholder="Opisz co brakuje..."
                    style={{ minHeight: 60 }}
                  />
                </div>
              )}

              <div className="form-group" style={{ marginBottom: 20 }}>
                <label className="form-label">Komentarz (opcjonalny)</label>
                <textarea
                  className="form-textarea"
                  value={statusComment}
                  onChange={e => setStatusComment(e.target.value)}
                  placeholder="Powód zmiany statusu..."
                  style={{ minHeight: 60 }}
                />
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setStatusModal(false)}>Anuluj</button>
                <button type="submit" className="btn btn-primary" disabled={!newStatus || changingStatus}>
                  {changingStatus ? 'Zmienianie...' : 'Zmień status'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
